import React, { useState, useEffect } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, ArrowUpRight, Info, Lock, Mail } from "lucide-react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Loading from "@/components/Loading";
import { signInWithEmailLink } from "@/ajax/auth/signInWithEmailLink";
import { prewarmGoogleAuth } from "@/ajax/auth/signInWithGoogle";
import EmailVerification from "@/components/EmailVerification/EmailVerification";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FORM_INPUT_CLASS_NAME } from "@/components/Forms/form-control-styles";
import { cn } from "@/lib/utils";
import { signInWithPassword } from "@/ajax/auth/signInWithPassword";
import { AuthError } from "@supabase/supabase-js";
import { getLastEmail, saveLastEmail } from "@/common/utils/lastEmail";
import { OrgPitchContent } from "@/features/signin/components/OrgPitchContent";
import RememberMe from "@/features/signin/components/RememberMe";
import { isNative } from "@/common/utils/environment";
import posthog from "posthog-js";
import { GoogleSignInButton } from "@/features/signin/components/GoogleSignInButton";
import { BlockedStorageWarning } from "@/features/signin/components/BlockedStorageWarning";
import { AppleSignInButton } from "@/features/signin/components/AppleSignInButton";
import { Storage } from "@/common/utils/storage";
import { parseRedirectTarget } from "@/common/utils/parseRedirectTarget";

const FormSchema = z.object({
  email: z.string().email("Invalid email address"),
  password: z.string().optional(),
});

type FormValues = z.infer<typeof FormSchema>;

const LOGO_PATH = "/static/images/logo.png";

export const Route = createFileRoute("/auth-org/signin/")({
  component: SignInPage,
  beforeLoad: async () => {
    await Storage.setItem("lastLogin", "org");
    posthog.reset();
  },

  validateSearch: z.object({
    l: z.string().optional(),
    redirect: z.string().regex(/^\//).optional(),
  }),
});

function SignInPage() {
  const [serverErrors, setServerErrors] = useState<Record<string, string[]>>(
    {},
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [emailSent, setEmailSent] = useState<null | string>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showPitch, setShowPitch] = useState(false);
  const [rememberMeOpen, setRememberMeOpen] = useState(false);
  const navigate = useNavigate();
  const search = Route.useSearch();

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      email: getLastEmail() || "",
    },
  });

  const email = watch("email");
  useEffect(() => {
    if (/demo.*?@saileapp\.com/.test(email)) {
      setShowPassword(true);
    }
  }, [email]);

  const checkConnection = () => {
    if (!navigator.onLine) {
      setServerErrors({
        _all: ["Please check your internet connection and try again"],
      });
      return false;
    }
    return true;
  };

  const onSubmit = async (data: FormValues) => {
    if (!checkConnection()) {
      return;
    }

    setIsSubmitting(true);
    setServerErrors({});

    let loginError: AuthError | null = null;
    if (showPassword) {
      const { error, data: loginData } = await signInWithPassword({
        email: data.email,
        password: data.password || "",
      });
      loginError = error;
      if (loginData?.user?.email) {
        saveLastEmail(loginData.user.email);
      }
    } else {
      const { error } = await signInWithEmailLink({
        email: data.email,
        shouldCreateUser: true,
      });
      loginError = error;
    }
    if (loginError) {
      setServerErrors({
        _all: [loginError.message],
      });
      setIsSubmitting(false);
      return;
    }

    if (showPassword) {
      handleComplete();
    } else {
      setEmailSent(data.email);
    }
  };

  const handleComplete = () => {
    if (isNative()) {
      handleRedirect();
    } else {
      setRememberMeOpen(true);
    }
  };

  const handleRedirect = () => {
    if (!search.redirect) {
      navigate({ to: "/org/home" });
      return;
    }
    const { to, search: redirectSearch } = parseRedirectTarget(search.redirect);
    navigate({ to, search: redirectSearch });
  };

  const handleBack = () => {
    setEmailSent(null);
    setIsSubmitting(false);
  };

  const handleResend = async () => {
    if (!checkConnection()) {
      return;
    }

    if (!emailSent) {
      console.error("No email to resend");
      return;
    }

    await signInWithEmailLink({
      email: emailSent,
      shouldCreateUser: true,
    });
  };

  const handleRememberMeComplete = () => {
    handleRedirect();
  };

  const handle3rdPartySigninError = (error: string) => {
    setServerErrors({
      _all: [error],
    });
  };

  useEffect(() => {
    prewarmGoogleAuth();
  }, []);

  if (emailSent) {
    return (
      <div>
        <EmailVerification
          onResend={handleResend}
          onBack={handleBack}
          onComplete={handleComplete}
          email={emailSent}
        />
        {!isNative() && (
          <RememberMe
            open={rememberMeOpen}
            onComplete={handleRememberMeComplete}
          />
        )}
      </div>
    );
  }

  if (showPitch) {
    return (
      <main className="min-h-dvh bg-primary px-6 pt-[calc(var(--ion-safe-area-top,0px)+1.5rem)] pb-[calc(var(--ion-safe-area-bottom,0px)+2rem)] text-white sm:px-10">
        <div className="mx-auto max-w-lg">
          <button
            type="button"
            onClick={() => setShowPitch(false)}
            className="mb-10 inline-flex min-h-11 items-center gap-2 rounded-lg text-sm font-medium text-white/90 transition-colors hover:text-white focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white"
          >
            <ArrowLeft className="size-4" aria-hidden="true" /> Back to sign in
          </button>
          <OrgPitchContent />
        </div>
      </main>
    );
  }

  return (
    <main className="flex min-h-dvh bg-white lg:gap-4 lg:p-4 xl:gap-6 xl:p-6">
      <section className="hidden w-1/2 flex-col justify-center rounded-3xl bg-primary px-10 py-14 text-white lg:flex xl:px-14">
        <div className="mx-auto w-full max-w-lg">
          <OrgPitchContent />
        </div>
      </section>

      <div className="flex w-full items-center justify-center px-6 pt-[calc(var(--ion-safe-area-top,0px)+2rem)] pb-[calc(var(--ion-safe-area-bottom,0px)+2rem)] sm:px-10 lg:w-1/2 lg:py-12">
        <div className="w-full max-w-sm">
          <div className="mb-8 flex flex-col items-center text-center">
            <img
              src={LOGO_PATH}
              alt="Saile"
              width={180}
              height={72}
              className="mb-10 h-auto w-36"
            />
            <div className="text-center">
              <h1 className="mb-3 text-3xl font-bold tracking-tight text-gray-900">
                Organization sign in
              </h1>
              <p className="text-sm leading-6 text-gray-500">
                Post jobs, hire providers, and manage your team.
              </p>

              <button
                type="button"
                onClick={() => setShowPitch(true)}
                className="mx-auto mt-3 inline-flex min-h-11 items-center justify-center gap-2 rounded-lg text-sm font-medium text-primary hover:underline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary lg:hidden"
              >
                <Info className="size-4" aria-hidden="true" /> Learn more about
                Saile
              </button>
            </div>
          </div>
          <BlockedStorageWarning />
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            <div className="space-y-3">
              <GoogleSignInButton
                onError={handle3rdPartySigninError}
                onComplete={handleComplete}
              />
              <AppleSignInButton
                onError={handle3rdPartySigninError}
                onComplete={handleComplete}
              />
            </div>
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t"></span>
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="bg-white px-4 text-gray-500">
                  or continue with email
                </span>
              </div>
            </div>

            <div className="space-y-2">
              <label
                htmlFor="org-email"
                className="block text-sm font-medium text-slate-700"
              >
                Email address
              </label>
              <div className="relative">
                <Input
                  id="org-email"
                  type="email"
                  autoComplete="email"
                  {...register("email")}
                  placeholder="you@example.com"
                  className={cn(FORM_INPUT_CLASS_NAME, "pr-12")}
                />
                <Mail
                  className="pointer-events-none absolute right-4 top-1/2 size-5 -translate-y-1/2 text-slate-500"
                  aria-hidden="true"
                />
              </div>
              {errors.email && (
                <p className="text-red-500 text-sm">{errors.email.message}</p>
              )}
            </div>
            {showPassword && (
              <div className="space-y-2">
                <label
                  htmlFor="org-password"
                  className="block text-sm font-medium text-slate-700"
                >
                  Password
                </label>
                <div className="relative">
                  <Input
                    id="org-password"
                    type="password"
                    autoComplete="current-password"
                    {...register("password")}
                    placeholder="Enter your password"
                    className={cn(FORM_INPUT_CLASS_NAME, "pr-12")}
                  />
                  <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-slate-500">
                    <Lock className="size-5" aria-hidden="true" />
                  </span>
                </div>
                {errors.email && (
                  <p className="text-red-500 text-sm">{errors.email.message}</p>
                )}
              </div>
            )}
            {serverErrors._all && (
              <p className="text-red-500 text-sm">{serverErrors._all[0]}</p>
            )}
            <Button type="submit" size="lg" className="w-full">
              {isSubmitting ? <Loading className="h-5 w-5" /> : "Sign In"}
            </Button>
          </form>

          <div className="mt-8 border-t border-gray-100 pt-6 text-center text-sm">
            <p className="mb-1 text-gray-500">
              Are you a healthcare professional?
            </p>
            <Link
              to="/auth-provider/signin"
              className="inline-flex min-h-11 items-center gap-1 font-medium text-primary underline-offset-4 hover:underline"
            >
              Provider sign in
              <ArrowUpRight className="size-4" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </div>
      {!isNative() && (
        <RememberMe
          open={rememberMeOpen}
          onComplete={handleRememberMeComplete}
        />
      )}
    </main>
  );
}
